import { create } from "zustand";
import { axiosInstance } from "../lib/axios.js";
import { tError } from "../lib/toast";
import io from "socket.io-client";

const protocol = window.location.protocol === "https:" ? "wss" : "ws";
const host = window.location.host;
const BASE_URL =
  import.meta.env.MODE === "development"
    ? "http://localhost:5001"
    : `${protocol}://${host}`;

const errMsg = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useAuthStore = create((set, get) => ({
  authUser: null,
  isSigningUp: false,
  isLoggingIng: false,
  isUpdatingProfile: false,
  isCheckingAuth: true,
  socket: null,
  onlineUsers: [],
  // Heartbeat that components showing simulated fake presence subscribe
  // to so they re-render at least once a minute and pick up new UTC
  // 10-minute buckets in lib/fakePresence.js. App.jsx starts the
  // interval at mount.
  presenceTick: 0,
  tickPresence: () => set((s) => ({ presenceTick: s.presenceTick + 1 })),

  checkAuth: async (telegramUser) => {
    try {
      if (!get().authUser && telegramUser) {
        get().telegramLogin(telegramUser);
        return;
      }
      const res = await axiosInstance.get("/auth/check");
      set({ authUser: res.data });
      get().connectSocket();
    } catch (error) {
      console.error("Error checking auth:", error);
      set({ authUser: null });
    } finally {
      set({ isCheckingAuth: false });
    }
  },

  signUp: async (data) => {
    set({ isSigningUp: true });
    try {
      const res = await axiosInstance.post("/auth/signup", data);
      set({ authUser: res.data });
    } catch (error) {
      tError(errMsg(error, "Sign up failed"));
    } finally {
      set({ isSigningUp: false });
    }
  },
  login: async (data) => {
    set({ isLoggingIn: true });
    try {
      const res = await axiosInstance.post("/auth/login", data);
      set({ authUser: res.data });
      get().connectSocket();
    } catch (error) {
      tError(errMsg(error, "Login failed, please try again"));
    } finally {
      set({ isLoggingIn: false });
    }
  },
  telegramLogin: async (data) => {
    set({ isLoggingIn: true });
    try {
      const res = await axiosInstance.post("/auth/telegramLogin", data);
      set({ authUser: res.data });
      get().connectSocket();
    } catch (error) {
      tError(errMsg(error, "Login failed, please try again"));
    } finally {
      set({ isLoggingIn: false });
    }
  },
  logout: async () => {
    try {
      await axiosInstance.get("/auth/logout");
      set({ authUser: null });
      get().disconnectSocket();
      localStorage.clear();
    } catch (error) {
      tError(errMsg(error, "Logout failed"));
    }
  },
  updateProfile: async (data) => {
    set({ isUpdatingProfile: true });
    try {
      const res = await axiosInstance.post("/auth/updateProfile", data);
      set({ authUser: res.data });
    } catch (error) {
      tError(errMsg(error, "Failed to save"));
    } finally {
      set({ isUpdatingProfile: false });
    }
  },
  updateAuthUser: async (data) => {
    set({ isUpdatingProfile: true });
    try {
      const res = await axiosInstance.post("/users/updateUser", data);
      set({ authUser: res.data });
    } catch (error) {
      tError(errMsg(error, "Failed to save"));
    } finally {
      set({ isUpdatingProfile: false });
    }
  },
  connectSocket: () => {
    const { authUser, socket } = get();
    if (!authUser || socket?.connected) return;
    const cur_socket = io(BASE_URL, {
      transports: ["websocket", "polling"],
      secure: true,
      reconnection: true,
      rejectUnauthorized: false,
      query: { userId: authUser._id },
    });
    cur_socket.connect();
    // Single-instance toast for socket issues — don't pile up reconnect errors.
    cur_socket.on("connect_error", (error) => {
      console.warn("Socket connection error:", error?.message || error);
      tError("Reconnecting…", { id: "socket-error", duration: 1500 });
    });
    cur_socket.on("connect", () => set({ socket: cur_socket }));
    cur_socket.on("getOnlineUsers", (userIds) => set({ onlineUsers: userIds }));
  },
  disconnectSocket: () => {
    const { socket } = get();
    if (socket?.connected) {
      socket.disconnect();
      set({ socket: null });
    }
  },
}));
